import { useEffect, useMemo, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Landmark,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  TrendingUp,
  WalletCards,
} from "lucide-react";
import "../styles/pages/investimentos.css";

const tipos = ["Ações", "FIIs", "Cripto", "Renda fixa", "ETF"];

function moeda(valor) {
  return Number(valor || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function numero(valor) {
  const n = String(valor || "").replace(".", "").replace(",", ".");
  return Number(n || 0);
}

function normalizarTicker(valor) {
  return String(valor || "").trim().toUpperCase().replace(/\s+/g, "");
}

async function buscarCotacoesBrapi(tickers) {
  const token = process.env.REACT_APP_BRAPI_TOKEN;
  const lista = tickers.map(normalizarTicker).filter(Boolean);

  if (!token || lista.length === 0) return {};

  const url = `https://brapi.dev/api/quote/${lista.join(",")}?token=${token}`;
  const resposta = await fetch(url);

  if (!resposta.ok) {
    throw new Error("Não foi possível buscar as cotações da BRAPI.");
  }

  const dados = await resposta.json();
  const mapa = {};

  (dados?.results || []).forEach((item) => {
    const symbol = normalizarTicker(item.symbol || item.stock || item.shortName);
    if (!symbol) return;

    mapa[symbol] = {
      ticker: symbol,
      nome: item.shortName || item.longName || symbol,
      preco: Number(item.regularMarketPrice || 0),
      variacaoDia: Number(item.regularMarketChangePercent || 0),
      moeda: item.currency || "BRL",
      atualizadoEm: item.regularMarketTime || item.updatedAt || null,
    };
  });

  return mapa;
}

export default function Investimentos({
  investimentos = [],
  salvarInvestimento,
  excluirInvestimento,
}) {
  const [ticker, setTicker] = useState("");
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("Ações");
  const [quantidade, setQuantidade] = useState("");
  const [precoMedio, setPrecoMedio] = useState("");
  const [busca, setBusca] = useState("");
  const [cotacoes, setCotacoes] = useState({});
  const [carregandoCotacao, setCarregandoCotacao] = useState(false);
  const [erroCotacao, setErroCotacao] = useState("");

  const tickersCarteira = useMemo(() => {
    return Array.from(
      new Set(
        investimentos
          .map((item) => normalizarTicker(item.ticker || item.nome))
          .filter(Boolean)
      )
    );
  }, [investimentos]);

  async function atualizarCotacoes() {
    if (!tickersCarteira.length) {
      setCotacoes({});
      return;
    }

    try {
      setCarregandoCotacao(true);
      setErroCotacao("");
      const mapa = await buscarCotacoesBrapi(tickersCarteira);
      setCotacoes(mapa);
    } catch (error) {
      console.error(error);
      setErroCotacao("Não consegui atualizar as cotações agora.");
    } finally {
      setCarregandoCotacao(false);
    }
  }

  useEffect(() => {
    atualizarCotacoes();
    const timer = setInterval(atualizarCotacoes, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickersCarteira.join(",")]);

  const carteira = useMemo(() => {
    return investimentos.map((item) => {
      const tickerAtivo = normalizarTicker(item.ticker || item.nome);
      const qtd = Number(item.quantidade || 0);
      const medio = Number(item.precoMedio || 0);
      const investido = Number(item.valorInvestido ?? item.valor ?? qtd * medio);
      const cotacao = cotacoes[tickerAtivo];
      const precoAtual = Number(cotacao?.preco || 0);
      const valorAtual = precoAtual > 0 && qtd > 0 ? qtd * precoAtual : investido;
      const resultado = valorAtual - investido;
      const rentabilidade = investido > 0 ? (resultado / investido) * 100 : 0;

      return {
        ...item,
        ticker: tickerAtivo,
        quantidade: qtd,
        precoMedio: medio,
        valorInvestido: investido,
        precoAtual,
        valorAtual,
        resultado,
        rentabilidade,
        variacaoDia: Number(cotacao?.variacaoDia || 0),
        cotacaoEncontrada: Boolean(cotacao?.preco),
      };
    });
  }, [investimentos, cotacoes]);

  const totais = useMemo(() => {
    const valorInvestido = carteira.reduce((acc, item) => acc + Number(item.valorInvestido || 0), 0);
    const valorAtual = carteira.reduce((acc, item) => acc + Number(item.valorAtual || 0), 0);
    const resultado = valorAtual - valorInvestido;
    const rentabilidade = valorInvestido > 0 ? (resultado / valorInvestido) * 100 : 0;

    return { valorInvestido, valorAtual, resultado, rentabilidade };
  }, [carteira]);

  const listaFiltrada = useMemo(() => {
    const texto = busca.toLowerCase().trim();
    if (!texto) return carteira;

    return carteira.filter((item) =>
      `${item.nome || ""} ${item.ticker || ""} ${item.tipo || ""}`
        .toLowerCase()
        .includes(texto)
    );
  }, [carteira, busca]);

  async function criar(e) {
    e.preventDefault();

    const tickerLimpo = normalizarTicker(ticker || nome);
    const qtd = numero(quantidade);
    const medio = numero(precoMedio);

    if (!tickerLimpo || qtd <= 0 || medio <= 0) return;

    if (typeof salvarInvestimento !== "function") {
      console.warn("Função salvarInvestimento não foi enviada para Investimentos.");
      return;
    }

    await salvarInvestimento({
      ticker: tickerLimpo,
      nome: nome.trim() || tickerLimpo,
      tipo,
      quantidade: qtd,
      precoMedio: medio,
      valorInvestido: qtd * medio,
    });

    setTicker("");
    setNome("");
    setTipo("Ações");
    setQuantidade("");
    setPrecoMedio("");
  }

  const positivo = totais.resultado >= 0;

  return (
    <section className="invest-page invest-live-page">
      <div className="invest-header">
        <div>
          <span>Carteira live</span>
          <h2>Investimentos</h2>
          <p>Acompanhe preço atual, lucro, prejuízo e rentabilidade automática.</p>
        </div>

        <button className="invest-refresh-btn" type="button" onClick={atualizarCotacoes}>
          {carregandoCotacao ? <Loader2 className="spin" size={20} /> : <RefreshCw size={20} />}
          Atualizar
        </button>
      </div>

      <section className="invest-hero">
        <div className="invest-hero-content">
          <span>Patrimônio atual</span>
          <strong>{moeda(totais.valorAtual)}</strong>
          <p>
            {carteira.length === 0
              ? "Cadastre um ticker para começar a acompanhar a carteira."
              : `${positivo ? "Lucro" : "Prejuízo"} de ${moeda(totais.resultado)} (${totais.rentabilidade.toFixed(2)}%).`}
          </p>

          <div className="invest-hero-tags">
            <small>
              <ShieldCheck size={15} />
              BRAPI conectada
            </small>
            <small>
              <BarChart3 size={15} />
              Atualiza a cada 60s
            </small>
          </div>

          {erroCotacao && <em className="invest-error">{erroCotacao}</em>}
        </div>

        <div className="invest-hero-visual" aria-hidden="true">
          <div className="invest-glow" />
          <div className="invest-chart-card">
            <div className="invest-chart-bars">
              <i style={{ height: "38%" }} />
              <i style={{ height: "62%" }} />
              <i style={{ height: "48%" }} />
              <i style={{ height: "78%" }} />
              <i style={{ height: "68%" }} />
            </div>
            <span className={positivo ? "gain" : "loss"}>{totais.rentabilidade.toFixed(2)}%</span>
          </div>
        </div>
      </section>

      <div className="invest-summary-grid live-summary-grid">
        <SummaryCard icon={<Landmark size={20} />} type="blue" label="Total investido" value={moeda(totais.valorInvestido)} />
        <SummaryCard icon={<WalletCards size={20} />} type="purple" label="Valor atual" value={moeda(totais.valorAtual)} />
        <SummaryCard
          icon={positivo ? <ArrowUpRight size={20} /> : <ArrowDownRight size={20} />}
          type={positivo ? "green" : "red"}
          label={positivo ? "Lucro total" : "Prejuízo total"}
          value={moeda(totais.resultado)}
        />
        <SummaryCard icon={<TrendingUp size={20} />} type="green" label="Rentabilidade" value={`${totais.rentabilidade.toFixed(2)}%`} />
      </div>

      <section className="invest-form-card">
        <div className="invest-form-header">
          <div>
            <h2>Novo investimento</h2>
            <p>Informe ticker, quantidade e preço médio. A rentabilidade vem automática.</p>
          </div>
          <div className="invest-form-icon"><Plus size={22} /></div>
        </div>

        <form className="invest-form invest-live-form" onSubmit={criar}>
          <div className="invest-field">
            <label>Ticker</label>
            <input value={ticker} onChange={(e) => setTicker(e.target.value)} placeholder="PETR4, VALE3, MXRF11..." />
          </div>
          <div className="invest-field">
            <label>Nome opcional</label>
            <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Petrobras" />
          </div>
          <div className="invest-field">
            <label>Tipo</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)}>
              {tipos.map((item) => <option key={item}>{item}</option>)}
            </select>
          </div>
          <div className="invest-field">
            <label>Quantidade</label>
            <input value={quantidade} onChange={(e) => setQuantidade(e.target.value)} placeholder="10" inputMode="decimal" />
          </div>
          <div className="invest-field">
            <label>Preço médio</label>
            <input value={precoMedio} onChange={(e) => setPrecoMedio(e.target.value)} placeholder="35,50" inputMode="decimal" />
          </div>
          <button className="invest-submit-btn" type="submit">
            <Plus size={18} /> Adicionar ativo
          </button>
        </form>
      </section>

      <section className="invest-list-card">
        <div className="invest-list-header">
          <div>
            <h2>Minha carteira</h2>
            <span>{listaFiltrada.length} ativo(s) encontrados.</span>
          </div>
          <label className="invest-search">
            <Search size={16} />
            <input placeholder="Buscar ativo..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </label>
        </div>

        <div className="invest-list">
          {listaFiltrada.length === 0 && (
            <div className="invest-empty">
              <TrendingUp size={28} />
              <strong>Nenhum ativo cadastrado</strong>
              <p>Cadastre PETR4, VALE3, MXRF11 ou outro ticker para começar.</p>
            </div>
          )}

          {listaFiltrada.map((item) => {
            const itemPositivo = Number(item.resultado || 0) >= 0;
            return (
              <article className="invest-item invest-live-item" key={item.id || item.ticker}>
                <div className="invest-item-left">
                  <div className="invest-item-icon"><TrendingUp size={18} /></div>
                  <div>
                    <strong>{item.ticker}</strong>
                    <span>{item.nome || item.tipo || "Investimento"}</span>
                  </div>
                </div>

                <div className="invest-live-columns">
                  <Info label="Qtd" value={item.quantidade} />
                  <Info label="Preço médio" value={moeda(item.precoMedio)} />
                  <Info label="Preço atual" value={item.cotacaoEncontrada ? moeda(item.precoAtual) : "Sem cotação"} />
                  <Info label="Investido" value={moeda(item.valorInvestido)} />
                  <Info label="Atual" value={moeda(item.valorAtual)} />
                  <Info
                    label={itemPositivo ? "Lucro" : "Prejuízo"}
                    value={`${moeda(item.resultado)} • ${item.rentabilidade.toFixed(2)}%`}
                    status={itemPositivo ? "gain" : "loss"}
                  />
                </div>

                {typeof excluirInvestimento === "function" && (
                  <button className="invest-delete-btn" onClick={() => excluirInvestimento(item.id)} type="button" aria-label="Excluir investimento">
                    <Trash2 size={16} />
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </section>
  );
}

function SummaryCard({ icon, label, value, type }) {
  return (
    <article className={`invest-summary-card ${type}`}>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="invest-card-icon">{icon}</div>
    </article>
  );
}

function Info({ label, value, status }) {
  return (
    <div className={`invest-info ${status || ""}`}>
      <small>{label}</small>
      <strong>{value}</strong>
    </div>
  );
}
